// VozReel.jsx
// El "vídeo" de contarle la comida a Wayo hablando. Igual que <AsistenteReel />,
// no es un vídeo: es la pantalla real de la app replicada en CSS y animada por
// pasos. Así el texto se lee nítido, no pesa nada y cambiar el copy no obliga a
// regrabar.
//
// Reutiliza el escenario de AsistenteReel.css (móvil, rótulos, barra de avance)
// y solo añade lo suyo: la hoja de dictar y la pantalla de resultados.
//
// Los números son los de una comida real analizada por la app
// ("dos tostadas con plátano y un café con leche"): 377 kcal que son la SUMA
// EXACTA de los tres alimentos, y unos macros que cuadran con Atwater
// (12x4 + 62x4 + 9x9 = 377). Si tocas uno, cuadra los demás.
import React, { useEffect, useRef, useState } from "react";
import "./AsistenteReel.css"; // el plató: móvil, rótulos, barra de avance
import "./VozReel.css"; // las dos pantallas de la app

const FRASE = "dos tostadas con plátano y un café con leche";

const ALIMENTOS = [
  { nombre: "Tostadas de pan", gramos: 60, kcal: 159 },
  { nombre: "Plátano", gramos: 150, kcal: 134 },
  { nombre: "Café con leche", gramos: 200, kcal: 84 },
];

// Cada plano es un estado completo de la pantalla: el componente se dibuja
// entero desde `plano` y no hay que deshacer nada al volver a empezar.
const PLANOS = [
  { ms: 2600, rotulo: ["Abres Wayo para ", "apuntar la comida", "."], vista: "inicio" },
  { ms: 2000, rotulo: ["Y ya no hace falta ", "ni la foto", "."], vista: "hoja" },
  { ms: 2600, rotulo: ["Se lo ", "dices", "."], vista: "hoja", grabando: true },
  { ms: 2600, rotulo: ["Y lo ", "escribe solo", "."], vista: "hoja", texto: true },
  { ms: 1600, rotulo: ["Wayo ", "echa las cuentas", "."], vista: "hoja", texto: true, calculando: true },
  { ms: 3800, rotulo: ["Y te lo ", "desglosa", ", alimento por alimento."], vista: "resultado" },
  { ms: 3200, rotulo: ["¿No te cuadra? ", "Lo corriges", "."], vista: "resultado", corrige: true },
  { ms: 2800, rotulo: ["Y va directo a ", "tu diario", "."], vista: "resultado", guarda: true },
];

const PLANO_FINAL = 5; // el que se enseña quieto si no hay animación
const DURACION = PLANOS.reduce((total, plano) => total + plano.ms, 0);

const prefiereMenosMovimiento = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function VozReel() {
  const [plano, setPlano] = useState(null);
  const [ciclo, setCiclo] = useState(0);
  const [activo, setActivo] = useState(false);
  const [escala, setEscala] = useState(null);
  const platoRef = useRef(null);

  // La escala se mide en JS, no en CSS: así el móvil SIEMPRE cabe. (Mismo
  // motivo que en AsistenteReel: con unidades de contenedor una pega de
  // soporte deja `scale()` inválido y el móvil sale a tamaño real.)
  useEffect(() => {
    const nodo = platoRef.current;
    if (!nodo) return undefined;

    const medir = () => {
      const { width, height } = nodo.getBoundingClientRect();
      if (!width) return;
      if (width >= 760) {
        if (!height) return;
        setEscala(Math.max(0.3, Math.min((height - 56) / 844, (width * 0.3) / 390)));
        return;
      }
      setEscala(Math.max(0.3, (width * 0.74) / 390));
    };

    medir();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  // Solo corre mientras se ve: fuera de pantalla no gasta nada.
  useEffect(() => {
    if (prefiereMenosMovimiento()) {
      setPlano(PLANO_FINAL);
      return undefined;
    }
    const nodo = platoRef.current;
    if (!nodo || typeof IntersectionObserver === "undefined") {
      setPlano(PLANO_FINAL);
      return undefined;
    }
    const observador = new IntersectionObserver(
      ([entrada]) => {
        setActivo(entrada.isIntersecting);
        if (entrada.isIntersecting) setPlano((actual) => (actual === null ? 0 : actual));
      },
      { threshold: 0.35 }
    );
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  useEffect(() => {
    if (!activo || plano === null) return undefined;
    const espera = setTimeout(() => {
      const siguiente = (plano + 1) % PLANOS.length;
      if (siguiente === 0) setCiclo((n) => n + 1);
      setPlano(siguiente);
    }, PLANOS[plano].ms);
    return () => clearTimeout(espera);
  }, [plano, activo]);

  const actual = PLANOS[plano ?? PLANO_FINAL];
  const arrancado = plano !== null;

  // Los rótulos giran como una rueda: en cada cambio conviven dos, el que sale
  // y el que entra.
  const [pila, setPila] = useState([]);
  useEffect(() => {
    if (plano === null) return;
    const entrante = PLANOS[plano].rotulo;
    if (!entrante) return;
    setPila((previa) => {
      const nuevo = { id: `${ciclo}-${plano}`, texto: entrante };
      return previa.length ? [previa[previa.length - 1], nuevo] : [nuevo];
    });
  }, [plano, ciclo]);

  const gramosTostadas = actual.corrige || actual.guarda ? 80 : 60;
  const kcalTostadas = actual.corrige || actual.guarda ? 212 : 159;
  const total = kcalTostadas + ALIMENTOS[1].kcal + ALIMENTOS[2].kcal;

  return (
    <div className="rl vz">
      <div className="rl-escenario" ref={platoRef} style={escala ? { "--s": escala } : undefined}>
        <div className="rl-rotulo-col">
          <div className="rl-rueda">
            {pila.map((item, indice) => (
              <p
                key={item.id}
                className={indice === pila.length - 1 ? "rl-rotulo rl-rotulo-entra" : "rl-rotulo rl-rotulo-sale"}
              >
                <span className="rl-rotulo-texto">
                  {item.texto[0]}
                  <em>{item.texto[1]}</em>
                  {item.texto[2]}
                </span>
              </p>
            ))}
          </div>
        </div>

        <div className="rl-movil-col">
          <div className="rl-movil">
            <div className="rl-screen vz-screen">
              <div className="rl-notch" />

              {/* ————— Pantalla de resultados ————— */}
              <div className={`vz-resultado${actual.vista === "resultado" ? " vz-visible" : ""}`}>
                <div className="vz-cabecera">Resultado del Análisis</div>

                <div className="vz-portada">
                  <span className="vz-plato">🍞</span>
                  <span className="vz-plato vz-plato-centro">🍌</span>
                  <span className="vz-plato">☕</span>
                </div>

                <div className="vz-cita">
                  <span className="vz-cita-label">🎙️ Lo que le has dicho a Wayo</span>
                  <span className="vz-cita-texto">«{FRASE}»</span>
                </div>

                <div className="vz-kcal">
                  <span className="vz-kcal-num">{total}</span>
                  <span className="vz-kcal-txt">Calorías</span>
                </div>

                <div className="vz-macros">
                  <span><b>62</b>Carbohidratos</span>
                  <span><b>9</b>Grasas</span>
                  <span><b>12</b>Proteínas</span>
                </div>

                <div className="vz-desglose">
                  <div className="vz-desglose-cab">
                    <span>LO QUE HA ENTENDIDO WAYO</span>
                  </div>
                  {ALIMENTOS.map((a, i) => {
                    const gramos = i === 0 ? gramosTostadas : a.gramos;
                    const kcal = i === 0 ? kcalTostadas : a.kcal;
                    const tocado = i === 0 && (actual.corrige || actual.guarda);
                    return (
                      <div className="vz-fila" key={a.nombre}>
                        <span className="vz-fila-txt">
                          {a.nombre}
                          <em className={tocado ? "vz-gramos vz-gramos-tocado" : "vz-gramos"}>{gramos} g</em>
                        </span>
                        <span className="vz-fila-kcal">{kcal}<i> kcal</i></span>
                      </div>
                    );
                  })}
                </div>

                <div className={`vz-guardar${actual.guarda ? " vz-guardar-on" : ""}`}>
                  <span className="vz-destino">Se guardará en <b>🌅 Desayuno</b></span>
                  <span className="vz-boton">Agregar al Diario</span>
                </div>
              </div>

              {/* ————— Punto de partida: la pantalla de escanear ————— */}
              {/* Se queda visible TAMBIEN mientras esta la hoja: en la app la
                  hoja se abre encima de esta pantalla, y sin ella queda un
                  bloque gris vacio detras. */}
              <div className={`vz-inicio${actual.vista === "inicio" || actual.vista === "hoja" ? " vz-visible" : ""}`}>
                <div className="vz-inicio-cab">Escanear Alimento</div>
                <span className="vz-inicio-titulo">Analiza tu comida con IA</span>
                <span className="vz-inicio-sub">
                  Hazle una foto, súbela desde la galería o simplemente cuéntale a Wayo lo que has comido.
                </span>
                <span className="vz-btn vz-btn-foto">Tomar Foto</span>
                <span className="vz-btn vz-btn-galeria">Galería</span>
                <span className="vz-btn vz-btn-voz">🎙️ Dile a Wayo que has comido</span>
              </div>

              {/* ————— Hoja de dictar ————— */}
              <div className={`vz-hoja-fondo${actual.vista === "hoja" ? " vz-visible" : ""}`}>
                <div className="vz-hoja">
                  <span className="vz-asa" />
                  <span className="vz-hoja-titulo">Dile a Wayo qué has comido</span>
                  <span className="vz-hoja-sub">
                    Descríbelo con tus palabras o dilo en voz alta. Wayo calcula las calorías y los macros.
                  </span>

                  <div className="vz-micro-caja">
                    <span className={`vz-micro${actual.grabando ? " vz-micro-rec" : ""}`}>
                      {actual.grabando ? (
                        <span className="vz-ondas" aria-hidden="true">
                          <i /><i /><i /><i /><i />
                        </span>
                      ) : (
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <rect x="9" y="2" width="6" height="11" rx="3" fill="#fff" stroke="none" />
                          <path d="M5 11a7 7 0 0014 0M12 18v3" />
                        </svg>
                      )}
                    </span>
                    <span className="vz-micro-pie">
                      {actual.grabando ? "Grabando 00:03 · toca para parar" : "Toca y cuéntaselo"}
                    </span>
                  </div>

                  <div className={`vz-campo${actual.texto ? " vz-campo-lleno" : ""}`}>
                    {actual.texto ? FRASE : "Ej: «He comido al mediodía arroz con pollo, una taza de arroz y tres pechugas»"}
                  </div>

                  <span className={`vz-cta${actual.texto ? " vz-cta-on" : ""}`}>
                    {actual.calculando ? "Calculando…" : "Calcular calorías y macros"}
                  </span>
                </div>
              </div>

              <span className="rl-homebar" />
            </div>
          </div>
        </div>

        <div className="rl-barra" aria-hidden="true">
          <span
            key={ciclo}
            className="rl-barra-relleno"
            style={{
              animationDuration: `${DURACION}ms`,
              animationPlayState: activo && arrancado ? "running" : "paused",
            }}
          />
        </div>
      </div>

      <p className="rl-pie">La pantalla tal cual está en la app. Las calorías son las de una comida real analizada por Wayo.</p>
    </div>
  );
}
